import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import React, { useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import PolicyReview from './PolicyReview';
import PolicyReviewEditLayout from './PolicyReviewEditLayout/PolicyReviewEditLayout';
import { getUserCurrency } from '../../utils/user';
import './PolicyReviewContainer.scss';
import { handleDashboardPolicyData, editPolicy } from '../../store/policy/duck';
import useMediaQuery from '@mui/material/useMediaQuery';
import { iOS, safari } from '../../utils/device';
import { getPolicyReview, resetPolicyReview, updatePolicyReviewInputs } from '../../store/policyReview/duck';
import { LOADING } from '../../constants/phase';
import { checkPolicy, isCategory, isSubCategory } from '../../utils/policy';
import { getPDF } from '../../store/pdf/api';
import { toast } from 'react-toastify';
import fx from 'money';
import moment from 'moment-timezone';
import PolicyReviewChooseClient from './PolicyReviewChooseClient/PolicyReviewChooseClient';
import PolicyReviewRemark from './PolicyReviewRemark/PolicyReviewRemark';
import {
  goToAddClient,
  goToAddPortfolio,
  isClientDisconnected,
  setClientIdLocalStorage
} from '../../components/ClientEntry/utils';
import { updateViewingClient } from '../../store/client/duck';
import AladdinHint from '../../components/AladdinHint/AladdinHint';
import { SHOW_SIDE_BAR_WIDTH } from '../App/component';
import { Button, CircularProgress, Switch, Typography, Grid, Box, Select, MenuItem } from '@mui/material';
import { getWebImageUrl } from '../../utils/image';
import PreviewWrapper from '../../pdf-templates/PreviewWrapper';
import { tracking } from '../../utils/tracking';
import { renderToString } from 'react-dom/server';
import MyEditor from '../../components/MyEditor/MyEditor';
import LanguageRadioGroup from '../../components/LanguageRadioGroup/LanguageRadioGroup';
import PresentModeSwitch from './PresentModeSwitch/PresentModeSwitch';
import { DEFAULT_CATEGORIES } from '../../constants/category';
import NativeOrWeb from '../../utils/native';
import ExportHintDialog from '../../components/ExportHintDialog/ExportHintDialog';
import { isSubscriptionAllowAction } from '../../utils/user-subscription-store';
import { isAdviserType } from '../../utils/user';
import { trackDirect } from '../../store/analytics/api';
import { trackViewership } from '../../utils/tracking/index';
import { CircularProgressWithLabel } from '../../components/CircularProgressWithLabel';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  slidingToggleButtonNoBorder,
  slidingToggleGroupNoBorder
} from '../../components/SlidingToggleButton/noBorderStyles';

const includeIOSFix = iOS() || safari();

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center'
  },
  toggleGroup: {
    position: 'relative',
    backgroundColor: '#f3f5fb',
    borderRadius: '8px !important',
    padding: 6,
    zIndex: 0,
    ...slidingToggleGroupNoBorder
  },
  toggleButton: {
    ...slidingToggleButtonNoBorder,
    borderRadius: '8px !important',
    padding: '0 20px',
    fontWeight: 'normal',
    fontSize: 14,
    height: 36,
    minWidth: 90,
    color: '#616161',
    textTransform: 'none',
    zIndex: 2,
    backgroundColor: 'transparent',
    transition: 'color 0.25s ease !important',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(57, 102, 248, 0.04)',
      border: 'none !important',
      borderColor: 'transparent !important',
      boxShadow: 'none'
    }
  },
  selected: {
    color: '#ffffff !important',
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important'
    }
  },
  slidingIndicator: {
    position: 'absolute',
    top: 6,
    bottom: 6,
    left: 6,
    width: 'calc(33.333% - 6px)',
    backgroundColor: '#3966f8',
    borderRadius: 8,
    boxShadow: '0 4px 10px rgba(57, 102, 248, 0.2)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1
  }
}));

const PolicyReviewContainer = props => {
  const {
    intl,
    history,
    userDetails,
    policyReview,
    getPhase,
    updatePhase,
    updatePolicyReviewInputs,
    viewingClient,
    fetchClientPhase,
    updateViewingClient,
    handleDashboardPolicyData,
    editPolicy,
    presentMode,
    loading,
    selectLanguage,
    selectLifeInsuredFilter,
    remark,
    getPolicyReview,
    updatePolicyReview,
    resetPolicyReview,
    ...rest
  } = props;

  const [progress, setProgress] = React.useState(0);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [version, setVersion] = useState(localStorage.getItem('version') || 'new');
  const timerRef = useRef(null);
  const { categories, portfolio } = policyReview || {};
  const isXsScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const { classes } = useStyles();

  let formattedCategories = (viewingClient ? categories : DEFAULT_CATEGORIES) || [];

  const onSelectClient = client => {
    updateViewingClient(client);
  };

  // Version toggle handler
  const switchVersion = (event, newVersion) => {
    if (newVersion) {
      setVersion(newVersion);
      localStorage.setItem('version', newVersion);
      tracking(`Policy Review: Version switched to ${newVersion}`);
    }
  };

  // const toggleShowSettings = () => setShowSettings(!presentMode);
  const switchPresentMode = event => updatePolicyReviewInputs('presentMode', event.target.checked);

  // Start progress timer
  const startProgressTimer = () => {
    setProgress(0);
    setIsGeneratingPDF(true);

    timerRef.current = setInterval(() => {
      setProgress(prevProgress => {
        if (prevProgress >= 96) {
          return 99; // Stay at 99% until PDF is actually generated
        }
        return prevProgress + 4;
      });
    }, 1000);
  };

  // Stop progress timer and complete to 100%
  const stopProgressTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setProgress(100);
    // Reset after a short delay to show completion
    setTimeout(() => {
      setProgress(0);
      setIsGeneratingPDF(false);
    }, 1000);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (userDetails) {
      let localLanguage = localStorage.getItem('policy-review-language');
      if (!localLanguage) {
        updatePolicyReviewInputs('selectLanguage', userDetails.language);
        localStorage.setItem('policy-review-language', userDetails.language);
      } else {
        updatePolicyReviewInputs('selectLanguage', localLanguage);
      }
    }
  }, [updatePolicyReviewInputs, userDetails]);

  useEffect(() => {
    if (!viewingClient) {
      return;
    }
    const viewingClientId = _.get(viewingClient, '_id');
    setClientIdLocalStorage(_.get(viewingClient, '_id'));
    const isDisconnected = isClientDisconnected(viewingClient);
    if (!isDisconnected && viewingClientId) {
      getPolicyReview(viewingClientId);
    }
  }, [viewingClient, getPolicyReview]);

  useEffect(() => {
    return () => {
      resetPolicyReview();
    };
  }, [resetPolicyReview]);

  useEffect(() => {
    trackViewership('policy-review', {
      'Element ID': 'policy-review',
      'Page Path': window.location.pathname,
      Interval: 180000
    });
  }, []);

  const partialScroll = useMediaQuery('(min-width: 768px)');
  const currency = getUserCurrency(userDetails);
  const clientName = _.get(viewingClient, 'factsheetId.name');
  const adviser = userDetails;
  const viewingClientId = _.get(viewingClient, '_id');
  const adviserTeamLogoUrl = _.get(adviser, 'teamLogoUrl');
  const isLoading =
    getPhase === LOADING || updatePhase === LOADING || fetchClientPhase === LOADING || loading || isGeneratingPDF;
  const back = () => history.goBack();

  const finalCategories = formattedCategories.map(category => ({
    image: category.image,
    subCategories: (category.subCategories || []).map(subCategory => ({
      image: subCategory.image,
      _id: subCategory._id,
      title: subCategory.title,
      value: subCategory.value,
      insurer: subCategory.insurer
    })),
    _id: category._id,
    title: category.title
  }));

  const generate = async options => {
    if (!loading && !isGeneratingPDF) {
      updatePolicyReviewInputs('loading', true);
      startProgressTimer(); // Start the progress timer when PDF generation begins

      try {
        options.clientName = clientName;
        options.currency = currency;
        options.categories = formattedCategories;
        options.portfolio = portfolio;
        options.currencyRates = fx.rates || {};
        options.adviser = adviser;
        options.viewingClientId = viewingClientId;
        options.version = version;

        const localStorageKeys = [
          'tabulator-policy-review-General-table-1-columns',
          'tabulator-policy-review-General-table-1-sort',
          'tabulator-policy-review-General-table-2-columns',
          'tabulator-policy-review-General-table-2-sort',
          'tabulator-policy-review-General-table-3-columns',
          'tabulator-policy-review-General-table-3-sort',

          'tabulator-policy-review-Personal-table-1-columns',
          'tabulator-policy-review-Personal-table-1-sort',
          'tabulator-policy-review-Personal-table-2-columns',
          'tabulator-policy-review-Personal-table-2-sort',
          'tabulator-policy-review-Personal-table-3-columns',
          'tabulator-policy-review-Personal-table-3-sort',

          'tabulator-policy-review-Company-table-1-columns',
          'tabulator-policy-review-Company-table-1-sort',
          'tabulator-policy-review-Company-table-2-columns',
          'tabulator-policy-review-Company-table-2-sort',
          'tabulator-policy-review-Company-table-3-columns',
          'tabulator-policy-review-Company-table-3-sort'
        ];

        options.localStorageData = {};

        localStorageKeys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              options.localStorageData[key] = JSON.parse(value);
            } catch (e) {
              options.localStorageData[key] = value;
            }
          }
        });

        // Tracking
        tracking(`Export Policy Review: export ${options.outputFormat}`, { adviser: adviser, version });

        const pdfBlob = await getPDF('policy-review', options);
        trackDirect('exportPolicyReview', { version });
        await NativeOrWeb.downloadFile(
          pdfBlob,
          `Review_${(clientName || 'Client').split(' ')[0]}_${moment().format('DDMMMYYYY')}.${options.outputFormat}`,
          options.outputFormat === 'pdf' ? 'application/pdf' : 'image/png'
        );

        stopProgressTimer(); // Stop timer and complete progress when PDF is ready
      } catch (error) {
        stopProgressTimer(); // Stop timer on error as well
        toast.error(error.message);
      } finally {
        updatePolicyReviewInputs('loading', false);
      }
    }
  };

  const addClient = () => {
    // Tracking
    tracking('Policy Review: Add Client button clicked');
    goToAddClient(history);
  };

  const addPolicy = (category, policy) => {
    // Tracking
    tracking('Policy Review: Add Policy button clicked');

    if (viewingClient) {
      let subCategory = undefined;
      if (policy && policy.subCategoryId) {
        if (isSubCategory(policy.subCategoryId, 'accident')) {
          category = formattedCategories.find(oCategory => isCategory(oCategory._id, 'general'));
        }

        if (category) {
          subCategory = category.subCategories.find(subCategory => subCategory._id === policy.subCategoryId);
        }
      }

      goToAddPortfolio(viewingClient, history, userDetails, false, {
        category: category,
        subCategory: subCategory
      });
    }
  };
  const onClickAddPolicy = event => addPolicy();

  const goToEditPolicy = policy => {
    if (viewingClient) {
      checkPolicy(policy, { history, handleDashboardPolicyData });
    }
  };

  // Edit remark
  const [remarkOpen, setRemarkOpen] = useState(false);
  const openRemark = () => {
    setRemarkOpen(true);
  };
  const closeRemark = () => {
    setRemarkOpen(false);
  };

  // Edit layout
  const [editLayoutOpen, setEditLayoutOpen] = useState(false);
  const openEditLayout = () => {
    setEditLayoutOpen(true);
  };
  const closeEditLayout = () => {
    setEditLayoutOpen(false);
  };

  // Export PDF
  const [hintDialog, setHintDialog] = useState(false);
  const openHintDialog = () => setHintDialog(true);
  const closeHintDialog = () => setHintDialog(false);

  const onClickPDF = () => {
    if (!isSubscriptionAllowAction({ type: 'core', nicknames: ['Pro', 'Ultra'] })) {
      return;
    }

    const isAdviser = isAdviserType(userDetails);
    if (isAdviser) {
      openHintDialog();
    }
  };
  const onConfirmGenerate = ({ password, isPrivateMode }) => {
    generate({
      ...props,
      title: encodeURIComponent(
        renderToString(<MyEditor readOnly={true} editorState={remark} textAlignment="center" />)
      ),
      language: selectLanguage,
      outputFormat: 'pdf',
      password: password,
      isPrivateMode: isPrivateMode
    });
  };

  // Change language
  const onChangeLanguage = event => {
    updatePolicyReviewInputs('selectLanguage', event.target.value);
    localStorage.setItem('policy-review-language', event.target.value);
  };

  return (
    <div>
      <div
        className={`policy-review-container${presentMode ? ' present-mode' : ''}${
          presentMode && window.innerWidth >= SHOW_SIDE_BAR_WIDTH ? ' negative-margin' : ''
        }`}
      >
        {includeIOSFix && (
          <style
            dangerouslySetInnerHTML={{
              __html: `
                        .policy-review-container table:not(html) {
                          transform: translate3d(0, 0, 0);
                          -webkit-transform: translate3d(0, 0, 0);
                          perspective: 1000px;
                          -webkit-perspective: 1000px;
                        }

                        .policy-review-container table {
                          border-collapse: separate;
                          border-spacing: 0;
                        }
                      `
            }}
          />
        )}

        {!presentMode && (
          <AladdinHint
            title={intl.formatMessage({ id: 'aladdin-demo-video' })}
            helpCenter="c94"
            style={{ marginLeft: 'auto', marginRight: 'auto' }}
          />
        )}

        <PolicyReviewEditLayout
          open={editLayoutOpen}
          closeDialog={closeEditLayout}
          updatePolicyReviewInputs={updatePolicyReviewInputs}
          currency={currency}
        />

        <ExportHintDialog
          open={hintDialog}
          onClose={closeHintDialog}
          onConfirm={onConfirmGenerate}
          extraContent={intl.formatMessage({ id: 'page-split-hint' })}
          shouldUsePassword={true}
        />

        <PolicyReviewRemark open={remarkOpen} closeDialog={closeRemark} />

        {!presentMode && (
          <div className="client-step">
            {!viewingClient && (
              <Typography color="error">{intl.formatMessage({ id: 'select-client-hint' })}</Typography>
            )}
            <Grid
              container
              spacing={1}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid>
                <PolicyReviewChooseClient selectedClient={viewingClient || ''} setSelectedClient={onSelectClient} />
              </Grid>
              <Grid>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={addClient}
                  style={{ padding: 8, lineHeight: 1, minWidth: 'unset', textTransform: 'unset' }}
                >
                  {intl.formatMessage({ id: 'add-client' })}
                </Button>
              </Grid>
            </Grid>
          </div>
        )}

        <div className="policy-step">
          <Grid
            container
            style={{ marginBottom: 8 }}
            sx={{
              alignItems: 'flex-start',
              justifyContent: !presentMode ? 'space-between' : 'flex-end'
            }}
          >
            {!presentMode && (
              <Grid size={isXsScreen ? 12 : undefined}>
                <Grid container wrap="nowrap" sx={{ flexDirection: 'column' }}>
                  <Grid>
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        alignItems: 'center'
                      }}
                    >
                      <Grid>
                        <Button
                          className="orange action-button"
                          variant="contained"
                          color="primary"
                          onClick={onClickAddPolicy}
                          disabled={!viewingClient}
                          style={{
                            padding: 3,
                            lineHeight: 1,
                            minWidth: 'unset',
                            textTransform: 'unset'
                          }}
                        >
                          {intl.formatMessage({ id: 'Add Policy' })}
                        </Button>
                      </Grid>
                      <Grid>
                        <Typography color="textSecondary" style={{ fontWeight: 'bold' }}>
                          {intl.formatMessage({ id: 'or-wording' })}
                        </Typography>
                      </Grid>
                      <Grid>
                        <Typography color="textSecondary">{intl.formatMessage({ id: 'pr-edit-hint-1' })}</Typography>
                      </Grid>
                      <Grid>
                        <img
                          src={getWebImageUrl('img/draft.png')}
                          alt="draft"
                          style={{ width: 16, verticalAlign: 'text-bottom' }}
                        />
                      </Grid>
                      <Grid>
                        <Typography color="textSecondary">{intl.formatMessage({ id: 'pr-edit-hint-2' })}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid
              container
              sx={{
                flexDirection: isXsScreen ? 'row' : 'column',
                justifyContent: isXsScreen ? 'space-between' : 'flex-end',
                alignItems: 'flex-end'
              }}
            >
              <Grid>
                <PresentModeSwitch
                  checked={presentMode}
                  onChange={switchPresentMode}
                  label={intl.formatMessage({ id: 'present-mode' })}
                />
              </Grid>

              <Grid>
                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ToggleButtonGroup
                    value={version}
                    exclusive
                    onChange={switchVersion}
                    aria-label="version toggle"
                    disabled={isLoading}
                    classes={{ root: classes.toggleGroup }}
                  >
                    <div
                      className={classes.slidingIndicator}
                      style={{
                        transform: `translateX(${version === 'new' ? 100 : version === 'compact' ? 200 : 0}%)`
                      }}
                    />
                    <ToggleButton value="old" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                      {intl.formatMessage({ id: 'old-version' })}
                    </ToggleButton>
                    <ToggleButton value="new" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                      {intl.formatMessage({ id: 'new-version' })}
                    </ToggleButton>
                    <ToggleButton value="compact" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                      {intl.formatMessage({ id: 'tot-version' })}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>

              {!presentMode && isXsScreen && (
                <Grid>
                  <LanguageRadioGroup value={selectLanguage} onChange={onChangeLanguage} />
                </Grid>
              )}

              {!presentMode && (
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: isXsScreen ? 'column-reverse' : 'row',
                    alignItems: isXsScreen ? 'start' : 'center',
                    width: '100%',
                    gap: 8
                  }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography style={{ fontSize: 16 }}>{intl.formatMessage({ id: 'Life Insured' })}</Typography>
                    <Select
                      id="life-insured-filter"
                      variant="outlined"
                      name="selectLifeInsuredFilter"
                      value={selectLifeInsuredFilter}
                      onChange={event => updatePolicyReviewInputs('selectLifeInsuredFilter', event.target.value)}
                      color={selectLifeInsuredFilter === 'All' ? 'primary' : 'error'}
                      style={{ height: 36, marginLeft: 8 }}
                    >
                      <MenuItem value="All">{intl.formatMessage({ id: 'All' })}</MenuItem>
                      <MenuItem value="Self">{intl.formatMessage({ id: 'Self' })}</MenuItem>
                      <MenuItem value="Not Me">{intl.formatMessage({ id: 'Not Me' })}</MenuItem>
                      <MenuItem value="No Idea">{intl.formatMessage({ id: 'No Idea' })}</MenuItem>
                    </Select>
                  </Box>

                  <Grid style={{ display: 'flex' }}>
                    {!isXsScreen && (
                      <LanguageRadioGroup
                        value={selectLanguage}
                        onChange={onChangeLanguage}
                        style={{ marginRight: 10 }}
                      />
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      className="new-export-button"
                      onClick={openEditLayout}
                      disabled={isLoading}
                      style={{ marginRight: 10 }}
                    >
                      {intl.formatMessage({ id: 'edit-layout' })}
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      className="new-export-button"
                      onClick={onClickPDF}
                      disabled={!viewingClient || isLoading}
                    >
                      {isLoading ? (
                        <CircularProgressWithLabel value={progress} size={progress ? 36 : 28} />
                      ) : (
                        `${intl.formatMessage({ id: 'Export_action' })} PDF`
                      )}
                    </Button>
                  </Grid>
                </Box>
              )}
            </Grid>
          </Grid>

          <PreviewWrapper locale={selectLanguage}>
            <PolicyReview
              history={history}
              currency={currency}
              categories={finalCategories}
              portfolio={portfolio}
              clientName={clientName}
              editable={!presentMode}
              disableActions={!viewingClient}
              partialScroll={partialScroll}
              adviser={adviser}
              viewingClientId={viewingClientId}
              adviserTeamLogoUrl={adviserTeamLogoUrl}
              explicitLoading={
                (getPhase === LOADING || updatePhase === LOADING || fetchClientPhase === LOADING) &&
                !(viewingClient === null || (viewingClient && viewingClient._id === _.get(portfolio, '[0].userId')))
              }
              onAddPolicy={addPolicy}
              onEditPolicy={goToEditPolicy}
              editPolicy={editPolicy}
              template={false}
              remarkOnClick={openRemark}
              remarkDisabled={!viewingClient || isLoading}
              title={encodeURIComponent(
                renderToString(<MyEditor readOnly={true} editorState={remark} textAlignment="center" />)
              )}
              version={version}
              getPolicyReview={getPolicyReview}
              selectLifeInsuredFilter={selectLifeInsuredFilter}
              {...rest}
            />
          </PreviewWrapper>
        </div>

        {!presentMode && (
          <div className="export-step">
            <PolicyReviewRemark onGenerate={generate} />
          </div>
        )}
      </div>
    </div>
  );
};

const container = connect(
  state => ({
    userDetails: state.user.userDetails,
    viewingClient: state.client.viewingClient,
    fetchClientPhase: state.client.fetchClientPhase,
    ...state.policyReview
  }),
  {
    handleDashboardPolicyData,
    editPolicy,
    updatePolicyReviewInputs,
    updateViewingClient,
    getPolicyReview,
    resetPolicyReview
  }
)(PolicyReviewContainer);

export default injectIntl(container);
